import { addSeconds, subSeconds } from '@luna/date';
import { logger } from '@luna/logger';
import { getEnv } from '@luna/env';

import { getTokenHeaders, getTokenUrl } from '../tokenFetchUtils';
import { getAuthData, setAuthData } from '../authDataService';
import type { RefreshTokenResponse } from '../strategy/tokenResponseTypes';
import type { RefreshTokenOptions, RefreshTokenUrlConfig } from './types';

const EXPIRATION_BUFFER = 30;

export function fetchRefreshToken(
    config: RefreshTokenUrlConfig,
    refreshToken: string,
): Promise<RefreshTokenResponse> {
    const body = new URLSearchParams();
    body.append('refresh_token', refreshToken);
    body.append('grant_type', 'refresh_token');
    body.append('client_id', config.clientId ?? getEnv().clientId);

    return fetch(getTokenUrl(config), {
        method: 'POST',
        headers: getTokenHeaders(),
        body,
    }).then((response) => {
        if (!response.ok) {
            throw response;
        }
        return response.json() as Promise<RefreshTokenResponse>;
    });
}

export async function fetchAndSaveRefreshToken(
    config: RefreshTokenUrlConfig,
    refreshToken: string,
): Promise<RefreshTokenResponse> {
    const refreshTokenResponse = await fetchRefreshToken(config, refreshToken);

    const newExp = addSeconds(new Date(), refreshTokenResponse.expires_in).toISOString();

    setAuthData({
        ...getAuthData(),
        refreshToken: refreshTokenResponse.refresh_token || refreshToken,
        tokenExpiration: newExp,
        token: refreshTokenResponse.access_token,
    });

    return refreshTokenResponse;
}

function getRefreshTimeWithBufferInMs(tokenExpiration: string): number {
    const tokenExpirationWithoutBuffer: Date = subSeconds(
        new Date(tokenExpiration),
        EXPIRATION_BUFFER,
    );
    return tokenExpirationWithoutBuffer.getTime() - Date.now();
}

/**
 * Initialises refresh token sequence (if refresh token is present), that calls refresh token endpoint
 * before access token expires. New access token and expiration is then saved to auth storage and
 * new iteration to refresh token begins.
 *
 * The premise it works on, is that previous access token is not invalidated on refresh, otherwise
 * we could end up in race-condition.
 */
export function initTokenRefresh(options: RefreshTokenOptions): void {
    const { refreshToken, tokenExpiration } = getAuthData();
    if (!refreshToken || !tokenExpiration) {
        return;
    }

    const expirationTime = getRefreshTimeWithBufferInMs(tokenExpiration);

    setTimeout(() => {
        // token should be refreshed
        fetchAndSaveRefreshToken(options.config, refreshToken)
            .then(() => {
                initTokenRefresh(options);
            })
            .catch((e) => {
                logger.error('Refreshing token failed', e);
                options.onLogout('http401');
            });
    }, expirationTime);
}
